/**
 * imageToStorage.js
 *
 * Utility per caricare immagini su Firebase Cloud Storage.
 * Sostituisce imageToBase64.js — le immagini vengono salvate come file
 * e nel database viene salvato solo l'URL pubblico (pochi caratteri).
 */

import { ref as storageRef, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage } from '../config/firebase';
import { compressImage } from './imageCompression';

// Cache lunga (30 giorni) per le immagini: sono file "immutabili" (ogni upload
// genera un nome file nuovo con timestamp), quindi è sicuro farle cachare a
// lungo da browser/CDN. Questo riduce di molto la banda scaricata dai
// visitatori che tornano più volte a sfogliare lo stesso catalogo.
const LONG_CACHE_METADATA = { cacheControl: 'public, max-age=2592000, immutable' };

/**
 * Carica un singolo file su Storage con compressione automatica.
 * @param {File}   file    - File immagine
 * @param {string} path    - Percorso su Storage (es. "cars/abc123/cover")
 * @param {Object} options - Opzioni di compressione
 * @returns {Promise<string>} URL pubblico permanente
 */
export const uploadImageToStorage = async (file, path, options = {}) => {
  const compressed = await compressImage(file, {
    maxSizeMB: options.maxSizeMB || 2,
    maxWidthOrHeight: options.maxWidthOrHeight || 2000,
    initialQuality: options.initialQuality || 0.92,
    ...options,
  });

  const ref = storageRef(storage, path);
  const snapshot = await uploadBytes(ref, compressed, LONG_CACHE_METADATA);
  return await getDownloadURL(snapshot.ref);
};

/**
 * Carica più file su Storage in parallelo.
 * @param {File[]}  files      - Array di file
 * @param {string}  basePath   - Cartella base (es. "cars/abc123/gallery")
 * @param {Object}  options    - Opzioni di compressione
 * @returns {Promise<string[]>} Array di URL pubblici
 */
export const uploadMultipleImagesToStorage = async (files, basePath, options = {}) => {
  const timestamp = Date.now();
  const uploads = files.map((file, i) =>
    uploadImageToStorage(file, `${basePath}/${timestamp}_${i}`, options)
  );
  return await Promise.all(uploads);
};

/**
 * Elimina un file da Storage tramite URL.
 * (Opzionale — usato per pulizia foto vecchie)
 * @param {string} url - URL pubblico del file
 */
export const deleteImageFromStorage = async (url) => {
  try {
    const ref = storageRef(storage, url);
    await deleteObject(ref);
  } catch (e) {
    // Non critico — il file potrebbe non esistere
    console.warn('deleteImageFromStorage:', e.message);
  }
};

/**
 * Carica un singolo file su Storage in DUE versioni: quella intera (per il
 * modal/galleria a schermo intero) e una miniatura leggera (per le card
 * elenco, dove non serve la risoluzione piena). Riduce di molto la banda
 * scaricata da chi sfoglia il catalogo pubblico, senza perdere qualità
 * quando l'utente apre davvero la foto.
 * @param {File}   file    - File immagine originale
 * @param {string} path    - Percorso base su Storage (es. "cars/abc123/gallery/img_1")
 * @param {Object} options - Opzioni di compressione per la versione intera
 * @returns {Promise<{url: string, thumbUrl: string}>}
 */
export const uploadImageWithThumbToStorage = async (file, path, options = {}) => {
  const [fullCompressed, thumbCompressed] = await Promise.all([
    // Versione "intera": mostrata nel carosello del dettaglio (alto al massimo ~400px, ~800px su retina), quindi
    // 1920px/qualità 0.85 restano nitidi anche su display 4K e pesano ~300-600 KB invece di 1-3 MB (prima 2560px/0.92,
    // tetto 4 MB). Ogni visita al dettaglio di un veicolo scarica una di queste foto: meno byte = meno banda Storage.
    // maxSizeMB è un tetto di sicurezza, non un obiettivo: con initialQuality alta la libreria riduce la qualità
    // solo se necessario.
    // fileType non viene forzato: lasciamo che browser-image-compression scelga
    // il formato migliore (JPEG per foto — evita artefatti WebP su dettagli fini).
    compressImage(file, {
      maxSizeMB: options.maxSizeMB || 1.5,
      maxWidthOrHeight: options.maxWidthOrHeight || 1920,
      initialQuality: options.initialQuality || 0.85,
      ...options,
    }),
    // Versione miniatura: usata nelle liste/card. 800px copre qualsiasi card
    // anche su display retina (2×) mantenendo nitidezza. 150KB è ancora molto
    // più leggera della versione intera (2MB) pur evitando la sgranatura che
    // si otteneva comprimendo aggressivamente a 320px/40KB.
    compressImage(file, {
      maxSizeMB: 0.15,
      maxWidthOrHeight: 800,
      initialQuality: 0.82,
    }),
  ]);

  const fullRef = storageRef(storage, path);
  const thumbRef = storageRef(storage, `${path}_thumb`);

  const [fullSnap, thumbSnap] = await Promise.all([
    uploadBytes(fullRef, fullCompressed, LONG_CACHE_METADATA),
    uploadBytes(thumbRef, thumbCompressed, LONG_CACHE_METADATA),
  ]);

  const [url, thumbUrl] = await Promise.all([
    getDownloadURL(fullSnap.ref),
    getDownloadURL(thumbSnap.ref),
  ]);

  return { url, thumbUrl };
};

/**
 * Carica più file su Storage in parallelo, ciascuno in versione intera + miniatura.
 * @param {File[]}  files      - Array di file
 * @param {string}  basePath   - Cartella base (es. "cars/abc123/gallery")
 * @param {Object}  options    - Opzioni di compressione per la versione intera
 * @returns {Promise<{urls: string[], thumbUrls: string[]}>}
 */
export const uploadMultipleImagesWithThumbsToStorage = async (files, basePath, options = {}) => {
  const timestamp = Date.now();
  const uploads = files.map((file, i) =>
    uploadImageWithThumbToStorage(file, `${basePath}/${timestamp}_${i}`, options)
  );
  const results = await Promise.all(uploads);
  return {
    urls: results.map(r => r.url),
    thumbUrls: results.map(r => r.thumbUrl),
  };
};

/**
 * Crea un'anteprima locale (Object URL) da un File, senza caricarlo.
 * Da revocare con URL.revokeObjectURL(url) quando non serve più.
 */
export const createLocalPreview = (file) => URL.createObjectURL(file);
